import { useCallback } from 'react';
import Checkbox from '../../checkbox/Checkbox';
import BaseFilter, { BaseFilterParams } from './BaseFilter';
import { useFilterContext } from '../FilterV2Context';

export type CheckboxOption = { label: string; value: string; defaultActive?: boolean };

export type CheckboxFilterProps = BaseFilterParams & { options: CheckboxOption[] };

/**
 * Filter option that renders a list of checkbox options.
 *
 * *Use Case*: A short fixed list of options that the user expects to be consistent.
 *
 * *Example*: Worksheet status is a fixed list of ["UNPREPARED",..., "REVIEWED"]. This is a perfect use case for a checkbox filter
 *
 * **NOTE**: All checked and none checked are equivalent.
 *
 * @param props CheckboxFilterProps
 */
function CheckboxFilter({ id, label, options }: CheckboxFilterProps): JSX.Element {
    const ctx = useFilterContext();
    const activeValues = ctx.getDraft(id, 'checkbox').values;

    const handleMenuClose = useCallback(() => {
        ctx.updateValues('checkbox', id, { values: activeValues });
    }, [activeValues, ctx, id]);

    function handleChange(e: React.ChangeEvent<HTMLInputElement>): void {
        const value = e.target.value;
        const checked = e.target.checked;

        const newActiveValues = checked ? [...activeValues, value] : [...activeValues].filter((v) => v !== value);
        ctx.updateValues('checkbox', id, { values: newActiveValues });
    }

    return (
        <BaseFilter id={id} label={label} activeCount={activeValues.length} onMenuClose={handleMenuClose} key={id}>
            <ul className="space-y-2">
                {options.map((opt) => {
                    return (
                        <li key={opt.value}>
                            <Checkbox
                                label={opt.label}
                                value={opt.value}
                                checked={activeValues.includes(opt.value)}
                                onChange={handleChange}
                            />
                        </li>
                    );
                })}
            </ul>
        </BaseFilter>
    );
}

export default CheckboxFilter;
